/**
 * Reviews/Feedback Service — Firebase v9 modular
 *
 * Backend implementation for product reviews system.
 * 
 * Firestore Collections:
 * - ProductReviews: Main reviews collection
 * - ReviewReplies: Threaded replies to reviews
 * 
 * Required Firestore Indexes:
 * - ProductReviews: productId (WHERE) + timestamp desc (ORDER BY)
 * - ProductReviews: productId (WHERE) + userId (WHERE)
 * - ReviewReplies: reviewId (WHERE) + timestamp asc (ORDER BY)
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  DocumentData,
  getCountFromServer,
  runTransaction,
} from 'firebase/firestore';
import { auth, db, uidOrThrow, dbGuard } from './firebaseConfig';
import type { ProductReview, ReviewReply, ReviewStats, ModerationStatus } from '../types/reviews';
import { withErrorHandling, AppError, ErrorCode } from '../lib/errors';
import { getDocsCacheFirst } from '../lib/firestoreCache';
import { verifyUserIdOwnership } from '../lib/security/idorProtection';
import { sanitizeText, sanitizeString, sanitizeUrl } from '../utils/sanitization';

// -----------------------------
// Types
// -----------------------------

export interface ReviewInput {
  productId: string;
  rating: number;
  comment?: string;
  imageUrls?: string[];
  userName?: string;
  userImageUrl?: string;
}

export interface ReviewUpdate {
  rating?: number;
  comment?: string;
  imageUrls?: string[];
}

export interface ReplyInput {
  reviewId: string;
  comment: string;
  isSeller: boolean;
  userName: string;
  userImageUrl?: string | null;
  parentReplyId?: string | null;
}

export interface PaginationCursor {
  timestamp: string; // ISO string
  id: string;
}

export interface PaginatedReviews {
  items: ProductReview[];
  nextCursor?: PaginationCursor;
  hasMore: boolean;
}

// -----------------------------
// Helpers
// -----------------------------

function tsToDate(value: unknown): Date {
  if (value === null || value === undefined) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && 'toDate' in value) {
    return (value as any).toDate();
  }
  return new Date();
}

function mapReview(id: string, data: DocumentData): ProductReview {
  return {
    id,
    productId: data.productId || '',
    userId: data.userId || '',
    userName: data.userName || '',
    userImageUrl: data.userImageUrl || '',
    rating: data.rating || 0,
    comment: data.comment || '',
    imageUrls: data.imageUrls || [],
    timestamp: tsToDate(data.timestamp),
    verifiedPurchase: data.verifiedPurchase || false,
    isReported: data.isReported || false,
    reportedBy: data.reportedBy || [],
    isHidden: data.isHidden || false,
    moderationStatus: data.moderationStatus || 'pending',
    moderatedAt: data.moderatedAt ? tsToDate(data.moderatedAt) : null,
    moderatedBy: data.moderatedBy || null,
    moderationReason: data.moderationReason || null,
    companyComment: data.companyComment || null,
    companyTimestamp: data.companyTimestamp ? tsToDate(data.companyTimestamp) : null,
    helpfulBy: Array.isArray(data.helpfulBy) ? data.helpfulBy.map(String) : [],
    helpfulCount: typeof data.helpfulCount === 'number'
      ? data.helpfulCount
      : Array.isArray(data.helpfulBy)
        ? data.helpfulBy.length
        : 0,
  };
}

function mapReply(id: string, data: DocumentData): ReviewReply {
  return {
    id,
    reviewId: data.reviewId || '',
    parentReplyId: data.parentReplyId || null,
    userId: data.userId || '',
    userName: data.userName || '',
    userImageUrl: data.userImageUrl || null,
    comment: data.comment || '',
    timestamp: tsToDate(data.timestamp),
    isSeller: data.isSeller || false,
  };
}

// -----------------------------
// Collections
// -----------------------------

const getProductReviewsCol = () => collection(db, 'ProductReviews');
const getReviewRepliesCol = () => collection(db, 'ReviewReplies');

// -----------------------------
// Core Functions
// -----------------------------

/**
 * Fetch reviews for a product with cursor pagination
 * Filters out hidden reviews for public queries
 * Note: Moderation status filtering is done in the application layer to use existing indexes
 */
export async function fetchReviews(
  productId: string,
  options: { limit?: number; cursor?: PaginationCursor } = {}
): Promise<PaginatedReviews> {
  return withErrorHandling(async () => {
    const limitCount = Math.min(options.limit || 10, 50); // Max 50 per page
    
    let q = query(
      getProductReviewsCol(),
      where('productId', '==', productId),
      where('isHidden', '==', false),
      orderBy('timestamp', 'desc'),
      limit(limitCount + 1)
    );

    // Handle cursor-based pagination
    if (options.cursor) {
      try {
        const cursorDoc = await getDoc(doc(getProductReviewsCol(), options.cursor.id));
        if (cursorDoc.exists()) {
          q = query(
            getProductReviewsCol(),
            where('productId', '==', productId),
            where('isHidden', '==', false),
            orderBy('timestamp', 'desc'),
            startAfter(cursorDoc),
            limit(limitCount + 1)
          );
        }
      } catch (error) {
        // SECURITY: Removed console.warn to prevent information leakage
      }
    }

    const snapshot = await getDocsCacheFirst(q);
    const docs = snapshot.docs;
    
    // Filter by moderation status in application layer to use existing indexes
    // Show approved reviews and pending reviews (for better UX during development)
    const filteredDocs = docs.filter(doc => {
      const data = doc.data();
      const status = data.moderationStatus;
      const isVisible = status === 'approved' || status === 'pending' || status === null || status === undefined;
      return isVisible;
    });
    
    const hasMore = filteredDocs.length > limitCount;
    const reviews = filteredDocs.slice(0, limitCount).map(d => mapReview(d.id, d.data()));

    const nextCursor = hasMore ? {
      timestamp: docs[limitCount - 1].data().timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
      id: docs[limitCount - 1].id
    } : undefined;

    // Removed verbose logging - reviews queries happen too frequently
    return { items: reviews, nextCursor, hasMore };
  }, {
    ctx: { feature: 'reviews', action: 'fetchReviews', extra: { productId } },
    domainHint: 'firestore'
  });
}

/**
 * Get review statistics for a product
 */
export async function getReviewStats(productId: string): Promise<ReviewStats> {
  return withErrorHandling(async () => {
    // SECURITY: Validate productId format to prevent injection attacks
    if (!productId || typeof productId !== 'string' || productId.length < 1 || productId.length > 1500) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid product ID format'
      });
    }
    
    if (!/^[a-zA-Z0-9._-]+$/.test(productId)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid product ID format'
      });
    }

    // Use count queries instead of reading all documents for better performance
    const baseQuery = query(
      getProductReviewsCol(),
      where('productId', '==', productId),
      where('isHidden', '==', false)
    );
    
    // Get total count
    const totalCountSnap = await getCountFromServer(baseQuery);
    const total = totalCountSnap.data().count;
    
    if (total === 0) {
      return { 
        averageRating: 0, 
        totalReviews: 0, 
        ratingCounts: [0, 0, 0, 0, 0], 
        verifiedPurchases: 0 
      };
    }
    
    // Get counts for each rating using count queries
    const [rating1Count, rating2Count, rating3Count, rating4Count, rating5Count, verifiedCount] = await Promise.all([
      getCountFromServer(query(baseQuery, where('rating', '==', 1))),
      getCountFromServer(query(baseQuery, where('rating', '==', 2))),
      getCountFromServer(query(baseQuery, where('rating', '==', 3))),
      getCountFromServer(query(baseQuery, where('rating', '==', 4))),
      getCountFromServer(query(baseQuery, where('rating', '==', 5))),
      getCountFromServer(query(baseQuery, where('verifiedPurchase', '==', true)))
    ]);
    
    const ratingCounts: [number, number, number, number, number] = [
      rating5Count.data().count, // [5,4,3,2,1] format
      rating4Count.data().count,
      rating3Count.data().count,
      rating2Count.data().count,
      rating1Count.data().count,
    ];
    
    const verified = verifiedCount.data().count;
    
    // Calculate average rating
    const sum = (ratingCounts[0] * 5) + (ratingCounts[1] * 4) + (ratingCounts[2] * 3) + (ratingCounts[3] * 2) + (ratingCounts[4] * 1);
    const averageRating = total ? Number((sum / total).toFixed(2)) : 0;
    
    return { 
      averageRating, 
      totalReviews: total, 
      ratingCounts, 
      verifiedPurchases: verified 
    };
  }, {
    ctx: { feature: 'reviews', action: 'getReviewStats', extra: { productId } },
    domainHint: 'firestore'
  });
}

/**
 * Check if a user can review a product (no existing review)
 * SECURITY: IDOR Protection - Validates userId matches authenticated user
 */
export async function canUserReview(userId: string, productId: string): Promise<boolean> {
  return withErrorHandling(async () => {
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    verifyUserIdOwnership(userId, {
      action: 'check review eligibility',
      resourceType: 'reviews'
    });

    const q = query(
      getProductReviewsCol(),
      where('productId', '==', productId),
      where('userId', '==', userId),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.empty; // Can review if no existing review found
  }, {
    ctx: { feature: 'reviews', action: 'canUserReview', userId, extra: { productId } },
    domainHint: 'firestore'
  });
}

/**
 * Add a new review for the current user
 * Enforces one review per user/product pair
 */
export async function addReview(input: ReviewInput): Promise<string> {
  return withErrorHandling(async () => {
    const userId = uidOrThrow();

    // Validate rating
    const rating = Number(input.rating);
    if (!(rating >= 1 && rating <= 5)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Rating must be between 1 and 5.'
      });
    }
    
    // SECURITY: Sanitize comment to prevent XSS
    const sanitizedComment = input.comment ? sanitizeText(input.comment.trim()).slice(0, 2000) : '';
    
    // Validate comment length
    if (input.comment && sanitizedComment.length === 0 && input.comment.trim().length > 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment contains invalid characters.'
      });
    }
    
    if (input.comment && input.comment.length > 2000) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment is too long (max 2000 characters).'
      });
    }
    
    // Validate image URLs
    const imageUrls = (input.imageUrls ?? []).slice(0, 6).map(String);

    // Check for existing review
    const canReview = await canUserReview(userId, input.productId);
    if (!canReview) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'You have already reviewed this product.'
      });
    }

    const id = crypto.randomUUID();
    
    // SECURITY: Sanitize userName and userImageUrl to prevent XSS
    const sanitizedUserName = input.userName ? sanitizeString(input.userName, 100) || '' : '';
    const sanitizedUserImageUrl = input.userImageUrl ? sanitizeUrl(input.userImageUrl, 500) : '';
    
    // Create review document
    const reviewRef = doc(getProductReviewsCol(), id);
    const reviewData = dbGuard.stampCreate({
      productId: input.productId,
      userId: userId,
      userName: sanitizedUserName,
      userImageUrl: sanitizedUserImageUrl,
      rating,
      comment: sanitizedComment,
      imageUrls,
      timestamp: serverTimestamp(),
      verifiedPurchase: false,
      isReported: false,
      reportedBy: [],
      isHidden: false,
      moderationStatus: 'pending',
      moderatedAt: null,
      moderatedBy: null,
      moderationReason: null,
      companyComment: null,
      companyTimestamp: null,
    });
    
    await setDoc(reviewRef, reviewData);

    return id;
  }, {
    ctx: { feature: 'reviews', action: 'addReview', userId: auth.currentUser?.uid, extra: { productId: input.productId } },
    domainHint: 'firestore'
  });
}

/**
 * Update an existing review
 * Only review owner or admins can update
 */
export async function updateReview(reviewId: string, patch: ReviewUpdate): Promise<void> {
  return withErrorHandling(async () => {
    const userId = uidOrThrow();

    // SECURITY: Validate reviewId to prevent injection attacks
    if (!reviewId || typeof reviewId !== 'string' || reviewId.length < 1 || reviewId.length > 1500) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid review ID format.'
      });
    }
    
    // SECURITY: Validate reviewId format (alphanumeric, dots, dashes, underscores only)
    if (!/^[a-zA-Z0-9._-]+$/.test(reviewId)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid review ID format.'
      });
    }

    // Get existing review
    const reviewRef = doc(getProductReviewsCol(), reviewId);
    const reviewSnap = await getDoc(reviewRef);
    
    if (!reviewSnap.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'firestore',
        message: 'Review not found.'
      });
    }

    const reviewData = reviewSnap.data();
    
    // SECURITY: Check ownership (only owner or admin can update)
    // Check if user is admin
    let isAdmin = false;
    try {
      const userDoc = await getDoc(doc(db, 'Users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdmin = userData?.role === 'admin';
      }
    } catch (error) {
      // If admin check fails, continue with ownership check only
      // SECURITY: Removed console.warn to prevent information leakage
    }
    
    if (reviewData.userId !== userId && !isAdmin) {
      // SECURITY: Log unauthorized access attempt
      // SECURITY: Removed console.warn to prevent information leakage
      throw new AppError({
        code: ErrorCode.DB_PERMISSION_DENIED,
        domain: 'firestore',
        message: 'You can only update your own reviews.'
      });
    }

    const updateData: any = {};
    
    // Validate and update rating
    if (patch.rating !== undefined) {
      if (!(patch.rating >= 1 && patch.rating <= 5)) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Rating must be between 1 and 5.'
        });
      }
      updateData.rating = patch.rating;
    }

    // Validate and update comment
    if (patch.comment !== undefined) {
      // SECURITY: Sanitize comment to prevent XSS
      const sanitizedComment = sanitizeText(patch.comment.trim()).slice(0, 2000);
      
      if (sanitizedComment.length === 0 && patch.comment.trim().length > 0) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Comment contains invalid characters.'
        });
      }
      
      if (patch.comment.length > 2000) {
        throw new AppError({
          code: ErrorCode.VALIDATION_FAILED,
          domain: 'validation',
          message: 'Comment is too long (max 2000 characters).'
        });
      }
      updateData.comment = sanitizedComment;
    }

    // Update image URLs
    if (patch.imageUrls !== undefined) {
      updateData.imageUrls = patch.imageUrls.slice(0, 6);
    }

    await updateDoc(reviewRef, updateData);
  }, {
    ctx: { feature: 'reviews', action: 'updateReview', userId: auth.currentUser?.uid, extra: { reviewId } },
    domainHint: 'firestore'
  });
}

/**
 * Delete a review
 * Only review owner or admins can delete
 */
export async function deleteReview(reviewId: string): Promise<void> {
  return withErrorHandling(async () => {
    const userId = uidOrThrow();

    // SECURITY: Validate reviewId to prevent injection attacks
    if (!reviewId || typeof reviewId !== 'string' || reviewId.length < 1 || reviewId.length > 1500) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid review ID format.'
      });
    }
    
    // SECURITY: Validate reviewId format (alphanumeric, dots, dashes, underscores only)
    if (!/^[a-zA-Z0-9._-]+$/.test(reviewId)) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid review ID format.'
      });
    }

    // Get existing review
    const reviewRef = doc(getProductReviewsCol(), reviewId);
    const reviewSnap = await getDoc(reviewRef);
    
    if (!reviewSnap.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'firestore',
        message: 'Review not found.'
      });
    }

    const reviewData = reviewSnap.data();
    
    // SECURITY: Check ownership (only owner or admin can delete)
    // Check if user is admin
    let isAdmin = false;
    try {
      const userDoc = await getDoc(doc(db, 'Users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdmin = userData?.role === 'admin';
      }
    } catch (error) {
      // If admin check fails, continue with ownership check only
      // SECURITY: Removed console.warn to prevent information leakage
    }
    
    if (reviewData.userId !== userId && !isAdmin) {
      // SECURITY: Log unauthorized access attempt
      // SECURITY: Removed console.warn to prevent information leakage
      throw new AppError({
        code: ErrorCode.DB_PERMISSION_DENIED,
        domain: 'firestore',
        message: 'You can only delete your own reviews.'
      });
    }

    await deleteDoc(reviewRef);
  }, {
    ctx: { feature: 'reviews', action: 'deleteReview', userId: auth.currentUser?.uid, extra: { reviewId } },
    domainHint: 'firestore'
  });
}

/**
 * Toggle helpful vote for a review (idempotent per user)
 */
export async function toggleHelpful(reviewId: string): Promise<{ helpfulCount: number; marked: boolean; userId: string }> {
  return withErrorHandling(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new AppError({
        code: ErrorCode.AUTH_INVALID_CREDENTIALS,
        domain: 'auth',
        message: 'You must be logged in to vote.'
      });
    }

    if (!reviewId || typeof reviewId !== 'string' || reviewId.length < 1 || reviewId.length > 1500) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Invalid review ID format.'
      });
    }

    const reviewRef = doc(getProductReviewsCol(), reviewId);

    const { count, marked } = await runTransaction(db, async (tx) => {
      const snap = await tx.get(reviewRef);
      if (!snap.exists()) {
        throw new AppError({
          code: ErrorCode.DB_NOT_FOUND,
          domain: 'firestore',
          message: 'Review not found.'
        });
      }

      const data = snap.data();
      const helpfulBy: string[] = Array.isArray(data.helpfulBy) ? data.helpfulBy.map(String) : [];
      const alreadyMarked = helpfulBy.includes(user.uid);
      const updated = alreadyMarked
        ? helpfulBy.filter((id) => id !== user.uid)
        : [...helpfulBy, user.uid];

      tx.update(reviewRef, {
        helpfulBy: updated,
        helpfulCount: updated.length,
      });

      return { count: updated.length, marked: !alreadyMarked };
    });

    return { helpfulCount: count, marked, userId: user.uid };
  }, {
    ctx: { feature: 'reviews', action: 'toggleHelpful', userId: auth.currentUser?.uid, extra: { reviewId } },
    domainHint: 'firestore'
  });
}

// -----------------------------
// Replies Management
// -----------------------------

/**
 * Fetch replies for a review
 */
export async function fetchReplies(reviewId: string): Promise<ReviewReply[]> {
  return withErrorHandling(async () => {
    const q = query(
      getReviewRepliesCol(),
      where('reviewId', '==', reviewId),
      orderBy('timestamp', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => mapReply(d.id, d.data()));
  }, {
    ctx: { feature: 'reviews', action: 'fetchReplies', extra: { reviewId } },
    domainHint: 'firestore'
  });
}

/**
 * Add a reply to a review
 */
export async function addReply(input: ReplyInput): Promise<string> {
  return withErrorHandling(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new AppError({
        code: ErrorCode.AUTH_INVALID_CREDENTIALS,
        domain: 'auth',
        message: 'You must be logged in to reply.'
      });
    }

    if (!input.reviewId) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'reviewId is required'
      });
    }

    if (!input.comment || input.comment.trim().length === 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment is required.'
      });
    }

    if (input.comment.length > 2000) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment is too long (max 2000 characters).'
      });
    }

    // SECURITY: Sanitize comment to prevent XSS
    const sanitizedComment = sanitizeText(input.comment.trim()).slice(0, 2000);
    
    if (sanitizedComment.length === 0 && input.comment.trim().length > 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment contains invalid characters.'
      });
    }

    const id = crypto.randomUUID();
    
    // SECURITY: Sanitize userName and userImageUrl to prevent XSS
    const sanitizedUserName = input.userName ? sanitizeString(input.userName, 100) || '' : '';
    const sanitizedUserImageUrl = input.userImageUrl ? sanitizeUrl(input.userImageUrl, 500) : null;
    
    await setDoc(doc(getReviewRepliesCol(), id), {
      reviewId: input.reviewId,
      parentReplyId: input.parentReplyId ?? null,
      userId: user.uid,
      userName: sanitizedUserName,
      userImageUrl: sanitizedUserImageUrl,
      comment: sanitizedComment,
      timestamp: serverTimestamp(),
      isSeller: Boolean(input.isSeller),
    });
    
    return id;
  }, {
    ctx: { feature: 'reviews', action: 'addReply', userId: auth.currentUser?.uid, extra: { reviewId: input.reviewId } },
    domainHint: 'firestore'
  });
}

// -----------------------------
// Legacy Functions (for backward compatibility)
// -----------------------------

/**
 * Legacy function - use fetchReviews instead
 */
export async function getProductReviews(
  productId: string,
  limitCount: number = 10,
  cursorId?: string
): Promise<{ reviews: ProductReview[]; nextCursorId?: string; hasMore: boolean }> {
  const result = await fetchReviews(productId, { 
    limit: limitCount, 
    cursor: cursorId ? { timestamp: new Date().toISOString(), id: cursorId } : undefined 
  });
  
  return {
    reviews: result.items,
    nextCursorId: result.nextCursor?.id,
    hasMore: result.hasMore
  };
}

/**
 * Legacy function - use getReviewStats instead
 */
export async function computeReviewStats(productId: string, cap = 500): Promise<ReviewStats> {
  return getReviewStats(productId);
}

/**
 * Legacy function - use fetchReplies instead
 */
export function streamReplies(reviewId: string, onChange: (replies: ReviewReply[]) => void): () => void {
  if (!reviewId) throw new AppError({
    code: ErrorCode.VALIDATION_FAILED,
    domain: 'validation',
    message: 'reviewId is required'
  });
  
  const qRef = query(
    getReviewRepliesCol(), 
    where('reviewId', '==', reviewId), 
    orderBy('timestamp', 'asc')
  );
  
  const unsub = onSnapshot(qRef, (qs) => {
    const items = qs.docs.map(d => mapReply(d.id, d.data()));
    onChange(items);
  }, (error) => {
    // Handle Firestore errors silently to prevent blocking the app
    // SECURITY: Removed console.warn to prevent information leakage
    // Don't propagate the error to avoid cascading AppError objects
    // Just call onChange with empty array to maintain consistent state
    onChange([]);
  });
  
  return unsub;
}

// Cache for active listeners to prevent duplicates
const activeListeners = new Map<string, {
  unsubscribe: () => void;
  callbacks: Set<(reviews: ProductReview[]) => void>;
}>();

/**
 * Legacy function - use fetchReviews instead
 * Now includes deduplication to prevent multiple listeners for the same productId
 */
export function streamReviews(productId: string, onChange: (reviews: ProductReview[]) => void): () => void {
  if (!productId) throw new AppError({
    code: ErrorCode.VALIDATION_FAILED,
    domain: 'validation',
    message: 'productId is required'
  });
  
  // Check if listener already exists for this productId
  const existing = activeListeners.get(productId);
  if (existing) {
    // Add callback to existing listener
    existing.callbacks.add(onChange);
    
    // Return cleanup function
    return () => {
      existing.callbacks.delete(onChange);
      // If no more callbacks, clean up the listener
      if (existing.callbacks.size === 0) {
        try {
          existing.unsubscribe();
        } catch (error) {
          // Ignore errors during cleanup
        }
        activeListeners.delete(productId);
      }
    };
  }
  
  // Create new listener
  const qRef = query(
    getProductReviewsCol(), 
    where('productId', '==', productId),
    where('isHidden', '==', false),
    orderBy('timestamp', 'desc'),
    limit(50)
  );
  
  const callbacks = new Set<(reviews: ProductReview[]) => void>([onChange]);
  
  const unsub = onSnapshot(qRef, (qs) => {
    try {
      // Filter by moderation status in application layer to use existing indexes
      // Show approved reviews and pending reviews (for better UX during development)
      const filteredDocs = qs.docs.filter(doc => {
        const data = doc.data();
        const status = data.moderationStatus;
        const isVisible = status === 'approved' || status === 'pending' || status === null || status === undefined;
        return isVisible;
      });
      
      // Filtered docs processed
      const items = filteredDocs.map(d => mapReview(d.id, d.data()));
      
      // Notify all callbacks
      callbacks.forEach(callback => {
        try {
          callback(items);
        } catch (error) {
          // Ignore errors in individual callbacks
        }
      });
    } catch (error) {
      // On error, notify all callbacks with empty array
      callbacks.forEach(callback => {
        try {
          callback([]);
        } catch (err) {
          // Ignore errors
        }
      });
    }
  }, (error) => {
    // Handle Firestore errors silently to prevent blocking the app
    // Notify all callbacks with empty array on error
    callbacks.forEach(callback => {
      try {
        callback([]);
      } catch (err) {
        // Ignore errors
      }
    });
  });
  
  // Store listener info
  activeListeners.set(productId, {
    unsubscribe: unsub,
    callbacks: callbacks
  });
  
  // Return cleanup function
  return () => {
    callbacks.delete(onChange);
    // If no more callbacks, clean up the listener
    if (callbacks.size === 0) {
      try {
        unsub();
      } catch (error) {
        // Ignore errors during cleanup
      }
      activeListeners.delete(productId);
    }
  };
}

/**
 * Legacy function - use addReply instead
 * SECURITY: IDOR Protection - Verifies ownership before allowing update
 */
export async function updateReply(replyId: string, patch: Pick<ReviewReply, 'comment'>): Promise<void> {
  return withErrorHandling(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new AppError({
        code: ErrorCode.AUTH_INVALID_CREDENTIALS,
        domain: 'auth',
        message: 'You must be logged in to update a reply.'
      });
    }
    
    if (!replyId) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'replyId is required'
      });
    }
    
    if (!patch.comment || patch.comment.trim().length === 0) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment is required.'
      });
    }
    
    if (patch.comment.length > 2000) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'Comment is too long (max 2000 characters).'
      });
    }
    
    // SECURITY: IDOR Protection - Get reply and verify ownership
    const replyRef = doc(getReviewRepliesCol(), replyId);
    const replySnap = await getDoc(replyRef);
    
    if (!replySnap.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'firestore',
        message: 'Reply not found.'
      });
    }
    
    const replyData = replySnap.data();
    
    // SECURITY: Check ownership (only owner or admin can update)
    // Check if user is admin
    let isAdmin = false;
    try {
      const userDoc = await getDoc(doc(db, 'Users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdmin = userData?.role === 'admin';
      }
    } catch (error) {
      // If admin check fails, continue with ownership check only
      // SECURITY: Removed console.warn to prevent information leakage
    }
    
    if (replyData.userId !== user.uid && !isAdmin) {
      // SECURITY: Log unauthorized access attempt
      // SECURITY: Removed console.warn to prevent information leakage
      throw new AppError({
        code: ErrorCode.DB_PERMISSION_DENIED,
        domain: 'firestore',
        message: 'You can only update your own replies.'
      });
    }
    
    await updateDoc(replyRef, { 
      comment: patch.comment, 
      timestamp: serverTimestamp() 
    });
  }, {
    ctx: { feature: 'reviews', action: 'updateReply', userId: auth.currentUser?.uid, extra: { replyId } },
    domainHint: 'firestore'
  });
}

/**
 * Legacy function
 * SECURITY: IDOR Protection - Verifies ownership before allowing delete
 */
export async function deleteReply(replyId: string): Promise<void> {
  return withErrorHandling(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new AppError({
        code: ErrorCode.AUTH_INVALID_CREDENTIALS,
        domain: 'auth',
        message: 'You must be logged in to delete a reply.'
      });
    }
    
    if (!replyId) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'replyId is required'
      });
    }
    
    // SECURITY: IDOR Protection - Get reply and verify ownership
    const replyRef = doc(getReviewRepliesCol(), replyId);
    const replySnap = await getDoc(replyRef);
    
    if (!replySnap.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'firestore',
        message: 'Reply not found.'
      });
    }
    
    const replyData = replySnap.data();
    
    // SECURITY: Check ownership (only owner or admin can delete)
    // Check if user is admin
    let isAdmin = false;
    try {
      const userDoc = await getDoc(doc(db, 'Users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdmin = userData?.role === 'admin';
      }
    } catch (error) {
      // If admin check fails, continue with ownership check only
      // SECURITY: Removed console.warn to prevent information leakage
    }
    
    if (replyData.userId !== user.uid && !isAdmin) {
      // SECURITY: Log unauthorized access attempt
      // SECURITY: Removed console.warn to prevent information leakage
      throw new AppError({
        code: ErrorCode.DB_PERMISSION_DENIED,
        domain: 'firestore',
        message: 'You can only delete your own replies.'
      });
    }
    
    await deleteDoc(replyRef);
  }, {
    ctx: { feature: 'reviews', action: 'deleteReply', userId: auth.currentUser?.uid, extra: { replyId } },
    domainHint: 'firestore'
  });
}

/**
 * Legacy function
 * SECURITY: IDOR Protection - Validates userId matches authenticated user
 */
export async function reportReview(reviewId: string, userId: string): Promise<void> {
  return withErrorHandling(async () => {
    if (!reviewId || !userId) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'reviewId and userId are required'
      });
    }

    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new AppError({
        code: ErrorCode.AUTH_INVALID_CREDENTIALS,
        domain: 'auth',
        message: 'User must be authenticated to report reviews'
      });
    }
    
    if (userId !== currentUser.uid) {
      throw new AppError({
        code: ErrorCode.DB_PERMISSION_DENIED,
        domain: 'firestore',
        message: 'You can only report reviews for your own account'
      });
    }
    
    const ref = doc(getProductReviewsCol(), reviewId);
    const snap = await getDoc(ref);
    
    if (!snap.exists()) {
      throw new AppError({
        code: ErrorCode.DB_NOT_FOUND,
        domain: 'firestore',
        message: 'This review no longer exists.'
      });
    }
    
    const data = snap.data();
    const reportedBy: string[] = Array.isArray(data.reportedBy) ? data.reportedBy.map(String) : [];
    
    if (!reportedBy.includes(userId)) {
      reportedBy.push(userId);
    }
    
    await updateDoc(ref, { reportedBy, isReported: reportedBy.length > 0 });
  }, {
    ctx: { feature: 'reviews', action: 'reportReview', userId: auth.currentUser?.uid, extra: { reviewId } },
    domainHint: 'firestore'
  });
}

/**
 * Legacy function
 */
export async function setModeration(reviewId: string, status: ModerationStatus, reason?: string): Promise<void> {
  return withErrorHandling(async () => {
    if (!reviewId) {
      throw new AppError({
        code: ErrorCode.VALIDATION_FAILED,
        domain: 'validation',
        message: 'reviewId is required'
      });
    }
    
    const ref = doc(getProductReviewsCol(), reviewId);
    const payload: {
      moderationStatus: ModerationStatus;
      moderatedAt: ReturnType<typeof serverTimestamp>;
      moderationReason: string | null;
    } = {
      moderationStatus: status ?? null,
      moderatedAt: serverTimestamp(),
      moderationReason: reason ?? null,
    };
    
    await updateDoc(ref, payload);
  }, {
    ctx: { feature: 'reviews', action: 'setModeration', userId: auth.currentUser?.uid, extra: { reviewId } },
    domainHint: 'firestore'
  });
}

// Legacy functions from reviews.service.ts - consolidated here

// Helper function to convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp && timestamp.toDate) {
    return timestamp.toDate();
  }
  return new Date(timestamp);
};

// Helper function to convert review document
const convertReviewDoc = (doc: any): ProductReview => {
  const data = doc.data();
  return {
    id: doc.id,
    productId: data.productId,
    userId: data.userId,
    userName: data.userName,
    userImageUrl: data.userImageUrl || data.userProfilePicture,
    rating: data.rating,
    comment: data.comment,
    imageUrls: data.imageUrls || data.images || [],
    timestamp: convertTimestamp(data.timestamp || data.createdAt),
    verifiedPurchase: data.verifiedPurchase || data.verified || false,
    isReported: data.isReported || false,
    reportedBy: data.reportedBy || [],
    isHidden: data.isHidden || false,
    moderationStatus: data.moderationStatus || 'approved'
  };
};

// Fetch featured testimonials from products
export const fetchFeaturedTestimonials = async (products: any[]): Promise<ProductReview[]> => {
  try {
    if (!products || products.length === 0) return [];

    // Get product IDs
    const productIds = products.map(p => p.id);
    
    // Query for reviews with high ratings
    const q = query(
      collection(db, 'ProductReviews'),
      where('productId', 'in', productIds.slice(0, 10)), // Firestore 'in' limit is 10
      where('rating', '>=', 4),
      orderBy('rating', 'desc'),
      orderBy('createdAt', 'desc'),
      limit(6)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(convertReviewDoc);
  } catch (error: any) {
    // Handle permission errors gracefully for non-authenticated users
    if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
      // SECURITY: Removed console.warn to prevent information leakage
      return [];
    }
    // SECURITY: Removed console.error to prevent information leakage
    return [];
  }
};

// Get reviews for a specific product (legacy function)
export const getProductReviewsLegacy = async (productId: string, limitCount: number = 10): Promise<ProductReview[]> => {
  try {
    // SECURITY: Validate inputs
    if (!productId || typeof productId !== 'string' || productId.length < 1 || productId.length > 1500) {
      return [];
    }
    
    if (!/^[a-zA-Z0-9._-]+$/.test(productId)) {
      return [];
    }

    // SECURITY: Validate limitCount to prevent excessive queries
    const validLimit = Math.max(1, Math.min(100, Math.floor(limitCount || 10)));

    const q = query(
      collection(db, 'ProductReviews'),
      where('productId', '==', productId),
      orderBy('createdAt', 'desc'),
      limit(validLimit)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(convertReviewDoc);
  } catch (error: any) {
    // Handle permission errors gracefully for non-authenticated users
    if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
      // SECURITY: Removed console.warn to prevent information leakage
      return [];
    }
    // SECURITY: Removed console.error to prevent information leakage
    return [];
  }
};

// Get reviews by user
// Note: Reviews are public data, but we validate userId format for security
export const getUserReviews = async (userId: string, limitCount: number = 20): Promise<ProductReview[]> => {
  try {
    // SECURITY: Validate inputs
    if (!userId || typeof userId !== 'string' || userId.length < 1 || userId.length > 1500) {
      return [];
    }
    
    // SECURITY: Validate userId format to prevent injection
    if (!/^[a-zA-Z0-9._-]+$/.test(userId)) {
      return [];
    }

    // SECURITY: Validate limitCount to prevent excessive queries
    const validLimit = Math.max(1, Math.min(100, Math.floor(limitCount || 20)));

    const q = query(
      collection(db, 'ProductReviews'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(validLimit)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(convertReviewDoc);
  } catch (error: any) {
    // Handle permission errors gracefully for non-authenticated users
    if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
      // SECURITY: Removed console.warn to prevent information leakage
      return [];
    }
    // SECURITY: Removed console.error to prevent information leakage
    return [];
  }
};

// Get recent reviews across all products
export const getRecentReviews = async (limitCount: number = 10): Promise<ProductReview[]> => {
  try {
    const q = query(
      collection(db, 'ProductReviews'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(convertReviewDoc);
  } catch (error: any) {
    // Handle permission errors gracefully for non-authenticated users
    if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
      // SECURITY: Removed console.warn to prevent information leakage
      return [];
    }
    // SECURITY: Removed console.error to prevent information leakage
    return [];
  }
};


